/*
 * Newsletter popup controller.
 *
 * Works alongside <dialog-component> (see dialog.js). Reads trigger/frequency
 * settings from data-* attributes on the <dialog-component> element and opens
 * the dialog accordingly. Respects the Shopify theme editor (always opens so
 * merchants can style the popup) and stores dismissal/subscription state in
 * localStorage.
 */
(() => {
  const STORAGE_VALUE_DISMISSED = 'dismissed';
  const STORAGE_VALUE_SUBSCRIBED = 'subscribed';
  const THEME_EDITOR = Boolean(window.Shopify && window.Shopify.designMode);

  const init = (host) => {
    if (host.dataset.newsletterInitialized === 'true') return;
    host.dataset.newsletterInitialized = 'true';

    const storageKey = host.dataset.storageKey || 'newsletter-popup';
    const trigger = host.dataset.trigger || 'delay';
    const delayMs = Number(host.dataset.delay || 5000);
    const scrollPercent = Number(host.dataset.scrollPercent || 50);
    const frequencyDays = Number(host.dataset.frequencyDays || 7);
    const hideAfterSubscribe = host.dataset.hideAfterSubscribe === 'true';
    const forceOpen = host.dataset.forceOpen === 'true';

    const readState = () => {
      try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return null;
        return JSON.parse(raw);
      } catch (err) {
        return null;
      }
    };

    const writeState = (value) => {
      try {
        localStorage.setItem(
          storageKey,
          JSON.stringify({ value, timestamp: Date.now() })
        );
      } catch (err) {
        /* storage unavailable - fail silently */
      }
    };

    const shouldShow = () => {
      if (THEME_EDITOR || forceOpen) return true;

      const state = readState();
      if (!state) return true;

      if (hideAfterSubscribe && state.value === STORAGE_VALUE_SUBSCRIBED) {
        return false;
      }

      if (frequencyDays <= 0) return true;

      const elapsedMs = Date.now() - (state.timestamp || 0);
      const cooldownMs = frequencyDays * 24 * 60 * 60 * 1000;
      return elapsedMs >= cooldownMs;
    };

    const playVideos = () => {
      host.querySelectorAll('video').forEach((video) => {
        try {
          // Re-kick the source if it didn't load while the <dialog> was display:none.
          if (video.readyState === 0) video.load();
          const p = video.play();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        } catch (err) {
          /* autoplay may be blocked; nothing we can do */
        }
      });
    };

    const openPopup = () => {
      if (!host.isConnected) return;
      if (typeof host.showDialog === 'function') {
        host.showDialog();
      } else {
        // Fallback in case dialog-component hasn't upgraded yet.
        const dlg = host.querySelector('dialog');
        if (dlg && typeof dlg.showModal === 'function') dlg.showModal();
      }
      // Defer so the dialog is fully visible before we start playback.
      requestAnimationFrame(() => requestAnimationFrame(playVideos));
    };

    // In case the browser did start autoplay (dialog was already open e.g. in
    // editor preview), also trigger on native dialog open events.
    host.addEventListener('dialog:open', playVideos);

    const scheduleOpen = () => {
      if (!shouldShow()) return;

      if (trigger === 'scroll') {
        const onScroll = () => {
          const scrolled =
            (window.scrollY / Math.max(1, document.body.scrollHeight - window.innerHeight)) * 100;
          if (scrolled >= scrollPercent) {
            window.removeEventListener('scroll', onScroll);
            openPopup();
          }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return;
      }

      if (trigger === 'exit') {
        const isTouch = window.matchMedia('(hover: none)').matches;
        if (isTouch) {
          setTimeout(openPopup, delayMs || 8000);
          return;
        }
        const onMouseOut = (event) => {
          if (event.relatedTarget || event.clientY > 0) return;
          document.removeEventListener('mouseout', onMouseOut);
          openPopup();
        };
        // Small grace period so it doesn't fire immediately on page load.
        setTimeout(() => document.addEventListener('mouseout', onMouseOut), 2000);
        return;
      }

      // Default: delay trigger.
      setTimeout(openPopup, delayMs);
    };

    // Track dismissal and subscription so we can respect frequency rules.
    host.addEventListener('dialog:close', () => {
      host.querySelectorAll('video').forEach((video) => {
        try {
          video.pause();
        } catch (err) {
          /* no-op */
        }
      });

      const state = readState();
      // Don't overwrite a successful subscription with a dismissal.
      if (state && state.value === STORAGE_VALUE_SUBSCRIBED) return;
      writeState(STORAGE_VALUE_DISMISSED);
    });

    // If the server rendered a success state (form.posted_successfully?), mark
    // as subscribed immediately so we don't nag the visitor next page load.
    if (host.querySelector('[data-newsletter-success]')) {
      writeState(STORAGE_VALUE_SUBSCRIBED);
    }

    // Theme editor: reopen when the merchant selects the section/block.
    if (THEME_EDITOR) {
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target && event.target.contains(host)) openPopup();
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scheduleOpen, { once: true });
    } else {
      scheduleOpen();
    }
  };

  const boot = () => {
    document.querySelectorAll('[data-newsletter-popup]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Re-init when sections are re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', boot);
})();
